<template>
  <div class="information-island">
    <p v-if="hasSubmitted">
      {{ model.confirmationText }}
    </p>
    <validation-observer
      v-else
      ref="observer"
      tag="oie-form-wrapper"
      class="information-island__form-container inputs-container"
      @submit="submit">
      <oie-base-input
        id="customerNumber"
        v-model="form.customerNumber"
        :label-text="`${tStatic('customerNumberLabel')}`"
        :placeholder="tStatic('customerNumberLabel')"></oie-base-input>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input
            id="companyName"
            v-model="form.name"
            :label-text="`${tStatic('companyNameLabel')}*`"
            :placeholder="tStatic('companyNamePlaceholder')"
            :error-messages="errors">
          </oie-base-input>
        </template>
      </validation-provider>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input
            id="orgNumber"
            v-model="form.orgNumber"
            :label-text="model.organisationNumberLabel + '*'"
            :placeholder="model.organisationNumberPlaceholder"
            :error-messages="errors">
          </oie-base-input>
        </template>
      </validation-provider>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input
            id="address"
            v-model="form.invoiceAddress.addressLine1"
            :label-text="`${tStatic('addressStreetLabel')}*`"
            :placeholder="`${tStatic('addressStreetPlaceholder')}`"
            :error-messages="errors">
          </oie-base-input>
        </template>
      </validation-provider>

      <validation-provider
        :rules="createPostalCodeValidationRule"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input
            id="postalCode"
            v-model="form.invoiceAddress.postalCode"
            :mask="createPostalCodeMask"
            :label-text="`${tStatic('addressPostalLabel')}*`"
            :placeholder="tStatic('addressPostalPlaceholder')"
            :error-messages="errors">
          </oie-base-input>
        </template>
      </validation-provider>

      <validation-provider
        rules="required|invalidchars"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input
            id="city"
            v-model="form.invoiceAddress.city"
            :label-text="`${tStatic('addressCityLabel')}*`"
            :placeholder="tStatic('addressCityPlaceholder')"
            :error-messages="errors">
          </oie-base-input>
        </template>
      </validation-provider>

      <validation-provider
        v-slot="{errors}"
        rules="required"
        slim>
        <oie-base-dropdown
          v-model="form.invoiceAddress.countryCode"
          :is-disabled="!hasMultipleMarkets"
          :label-text="`${tStatic('addressCountryLabel')}*`"
          :options="countryList"
          :initial-text="`${tStatic('addressCountryLabel')}*`"
          :use-filter-functionality="countryList.length > 10"
          :filter-input-placeholder="tStatic('addressCountryPlaceholder')"
          :error-messages="errors">
        </oie-base-dropdown>
      </validation-provider>

      <validation-provider
        rules="required"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input
            id="contactPerson"
            v-model="form.contactPerson"
            :label-text="model.contactPersonLabel + '*'"
            :placeholder="model.contactPersonPlaceholder"
            :error-messages="errors"></oie-base-input>
        </template>
      </validation-provider>

      <validation-provider
        v-slot="{errors}"
        rules="required|numeric"
        slim>
        <oie-base-input
          id="contract-customer-form-phone"
          v-model="phone"
          :label-text="`${tStatic('phoneLabel')}*`"
          :placeholder="tStatic('phonePlaceholderLabel')"
          :max-length="27"
          :error-messages="errors"
          :prefix-value="selectedActiveCountry ? selectedActiveCountry.callingCode : null"></oie-base-input>
      </validation-provider>

      <validation-provider
        rules="required|email"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-input
            id="email"
            v-model="form.email"
            :label-text="`${tStatic('email')}*`"
            :placeholder="tStatic('email')"
            :max-length="100"
            :error-messages="errors">
          </oie-base-input>
        </template>
      </validation-provider>

      <oie-base-input
        id="errand"
        v-model="form.subject"
        :textarea="true"
        :label-text="model.errandLabel"
        :placeholder="model.errandPlaceholder"
        :error-messages="errors"
        :max-length="500"></oie-base-input>

      <oie-base-checkbox
        v-model="form.contactMe"
        :errors="errors"
        :text="model.contactMeText"></oie-base-checkbox>

      <validation-provider
        rules="checked"
        slim
        mode="passive">
        <template slot-scope="{ errors }">
          <oie-base-checkbox v-model="form.termsAccepted" :errors="errors" :text="model.approveTermsContent"></oie-base-checkbox>
        </template>
      </validation-provider>

      <oie-base-action type="submit">
        {{ tStatic('sendButton') }}
      </oie-base-action>
    </validation-observer>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import countryHandling from '@/mixins/country-handling'
import utils from '@/core/utils'
import _ from 'lodash'

export default {
  mixins: [countryHandling],
  props: {
    model: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      form: {
        contactPerson: '',
        termsAccepted: false,
        contactMe: false,
        customerNumber: '',
        email: '',
        invoiceAddress: {
          addressLine1: '',
          city: '',
          countryName: '',
          countryCode: '',
          name: '',
          postalCode: ''
        },
        name: '',
        phone: '',
        vatNumber: '',
        orgNumber: '',
        subject: ''
      },
      hasSubmitted: false
    }
  },
  computed: {
    phone: {
      get() {
        return this.selectedActiveCountry ? utils.extractPhoneNumberFromFormating(this.form.phone, this.selectedActiveCountry.callingCode) : this.form.phone
      },
      set(value) {
        this.selectedActiveCountry ? this.form.phone = utils.savePhoneNumberWithFormating(value, this.selectedActiveCountry.callingCode) : this.form.phone = value
      }
    },
    createPostalCodeValidationRule() {
      return this.selectedCountry ? `required|postalCode:${this.selectedCountry.value},${this.selectedCountry.postalCodeRule}` : null
    },
    createPostalCodeMask() {
      return this.selectedCountry ? this.selectedCountry.postalCodeMask : null
    }
  },
  watch: {
    'form.invoiceAddress.countryCode': function (newVal, oldVal) {
      if(newVal) this.setActiveCountry(newVal)
    }
  },
  async beforeMount() {
    if(this.clientModel.isAuthenticated) {
      const userInfo = (await httpService.get('forms/currentuserinfo')).data
      _.merge(this.form, userInfo)
    } else {
      if(this.defaultCountry) this.form.invoiceAddress.countryCode = this.defaultCountry.value
    }
  },
  methods: {
    async submit() {
      const valid = await this.$refs.observer.validate()
      if(valid) {
        await httpService.post('forms/sendcontractapplicantform', { ...this.form, blockId: this.blockId })
        this.hasSubmitted = true
      }
    }
  }
}
</script>
